import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Paper,
  Typography,
  IconButton,
  Rating,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Seo from "../components/Seo";
import TrustPanel from "../components/TrustPanel";
import { SITE } from "../data/site";
import { websiteReviews } from "../data/reviews";
import { buildBreadcrumbSchema, buildServiceSchema, buildWebPageSchema } from "../utils/schema";
import "./Services.css";

// Add these when you have examples ready
// const image1 = `${process.env.PUBLIC_URL}/assets/websiteExample1.png`;
// const image2 = `${process.env.PUBLIC_URL}/assets/websiteExample2.png`;
// const image3 = `${process.env.PUBLIC_URL}/assets/websiteExample3.png`;
const heroBackgroundImage = `${process.env.PUBLIC_URL}/assets/Logo.svg`;

function ImageCarousel({ images }) {
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    if (!Array.isArray(images)) return;
    images.forEach((img) => {
      const preloader = new Image();
      preloader.src = img.image;
    });
  }, [images]);

  if (!images || images.length === 0) return null;

  const prev = () => setIdx((i) => (i - 1 + images.length) % images.length);
  const next = () => setIdx((i) => (i + 1) % images.length);

  return (
    <Paper className="bws-gsl-carousel" elevation={0}>
      <div className="bws-gsl-carouselInner">
        <IconButton className="bws-gsl-carouselBtn" onClick={prev} aria-label="Previous example">
          <ChevronLeftIcon />
        </IconButton>

        <div className="bws-gsl-carouselFrame">
          {images.map((img, i) => (
            <img
              key={i}
              className={`bws-gsl-carouselImg ${i === idx ? "is-active" : "is-hidden"}`}
              src={img.image}
              alt={img.altText}
              loading="eager"
              decoding="async"
            />
          ))}
        </div>

        <IconButton className="bws-gsl-carouselBtn" onClick={next} aria-label="Next example">
          <ChevronRightIcon />
        </IconButton>
      </div>

      <div className="bws-gsl-carouselDots">
        {images.map((_, i) => (
          <button
            key={i}
            type="button"
            className={`bws-gsl-dot ${i === idx ? "is-active" : ""}`}
            aria-label={`View example ${i + 1}`}
            onClick={() => setIdx(i)}
          />
        ))}
      </div>
    </Paper>
  );
}

export default function Websites() {
  const safeReviews = Array.isArray(websiteReviews) ? websiteReviews : [];
  const title = "Websites — Small Business Websites, Landing Pages, Shopify Help";
  const description =
    "Professional websites, landing pages, and Shopify improvements that make your business look more polished, clearer, and easier for customers to use.";
  const canonical = "https://brainwavesolutions.net/services/websites";
  const structuredData = [
    buildWebPageSchema({ title, description, canonical }),
    buildBreadcrumbSchema([
      { name: "Home", url: SITE.url },
      { name: "Services", url: `${SITE.url}/services` },
      { name: "Websites", url: canonical },
    ]),
    buildServiceSchema({
      name: "Websites",
      description,
      canonical,
      offerLabel: "$150–$2,500+",
      serviceType: "Small business websites and landing pages",
    }),
  ];

  const images = [
    // {
    //   image: image1,
    //   altText:
    //     "Small business website homepage with clear navigation, service sections, and strong call to action.",
    // },
    // {
    //   image: image2,
    //   altText:
    //     "Responsive website layout showing polished design sections and mobile-friendly structure.",
    // },
    // {
    //   image: image3,
    //   altText:
    //     "Professional landing page design with modern layout, service highlights, and conversion-focused content.",
    // },
  ].filter((img) => img.image);

  return (
    <>
      <Seo
        title={title}
        description={description}
        canonical={canonical}
        structuredData={structuredData}
      />

      <Box className="bws-gsl-page">
        <Container maxWidth="lg">
          <div className="bws-gsl-hero">
            <div
              className="bws-gsl-heroMain"
              style={{ "--gsl-bg-image": `url(${heroBackgroundImage})` }}
            >
              <Typography variant="h1" className="bws-gsl-title">
                Websites that look more polished, feel easier to use, and do a better job for your business
              </Typography>

              <p className="bws-gsl-lead">
                A lot of business websites are technically live, but they still are not doing the job well. Important
                information is hard to find, the structure feels cluttered, the design does not inspire confidence, or
                the page does not clearly guide visitors toward the next step.
              </p>

              <p className="bws-gsl-lead bws-gsl-leadTight">
                I help businesses improve websites, landing pages, and Shopify stores so they feel cleaner, more
                professional, and easier to navigate. The goal is not just to make a site look better — it is to make
                it more useful, more persuasive, and easier for customers to trust.
              </p>

              <div className="bws-gsl-badges">
                <span className="bws-gsl-badge">Small business websites</span>
                <span className="bws-gsl-badge">Landing pages</span>
                <span className="bws-gsl-badge">Shopify help</span>
                <span className="bws-gsl-badge">Site improvements</span>
              </div>

              <div className="bws-gsl-ctaRow">
                <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
                  Get help with your website
                </Button>
              </div>

              <div className="bws-gsl-miniTrust">
                <span>Businesses &amp; individuals</span>
                <span>•</span>
                <span>Clear communication</span>
                <span>•</span>
                <span>Practical solutions</span>
              </div>
            </div>

            <div className="bws-gsl-sideStack">
              <TrustPanel
                title="Work directly with Nate"
                body="You’re not sending your project into a big agency pipeline, you will work directly with me."
                badges={["Based in the USA", "Native English speaker"]}
              />

              <Paper className="bws-gsl-sideCard" elevation={0}>
                <Typography className="bws-gsl-sideTitle">Common website problems I solve</Typography>
                <ul className="bws-gsl-sideList">
                  <li>Sites that look outdated or feel hard to use</li>
                  <li>Pages that do not clearly explain the offer or service</li>
                  <li>Weak page structure and confusing layouts</li>
                  <li>Shopify theme tweaks, fixes, and polish</li>
                  <li>Landing pages that need to convert better</li>
                </ul>

                <Typography className="bws-gsl-sideNote">
                  Best first step: send a message with your website URL, what feels off, and what you want the site to do better.
                </Typography>
              </Paper>
            </div>
          </div>

          <div className="bws-gsl-strip">
            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Look more polished</Typography>
              <Typography className="bws-gsl-stripText">
                Improve layout, presentation, and clarity so the site feels more trustworthy and professional.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Make it easier to use</Typography>
              <Typography className="bws-gsl-stripText">
                Help visitors find what they need faster and move toward the next step more naturally.
              </Typography>
            </Paper>

            <Paper className="bws-gsl-stripCard" elevation={0}>
              <Typography className="bws-gsl-stripTitle">Support the full site</Typography>
              <Typography className="bws-gsl-stripText">
                Stronger structure, clearer messaging, and better usability also support SEO and overall performance.
              </Typography>
            </Paper>
          </div>

          {images.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Examples:</Typography>
                <p className="bws-gsl-p">
                  Examples of website layouts, landing page improvements, and site polish work.
                </p>
              </div>

              <ImageCarousel images={images} />
            </div>
          )}

          <div className="bws-gsl-section">
            <div className="bws-gsl-grid">
              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">What I help with</Typography>
                  <ul className="bws-gsl-list">
                    <li>Small business websites and landing pages</li>
                    <li>Website cleanup, structure, and presentation</li>
                    <li>Shopify setup, tweaks, and theme improvements</li>
                    <li>Clarity improvements so the offer is easier to understand</li>
                    <li>Responsive layout and overall user experience improvements</li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bws-gsl-card" elevation={0}>
                <CardContent className="bws-gsl-cardContent">
                  <Typography className="bws-gsl-h2">Good fit for</Typography>
                  <ul className="bws-gsl-list">
                    <li>Businesses whose site feels outdated or unclear</li>
                    <li>Service providers who need a stronger landing page</li>
                    <li>Shop owners who need practical Shopify improvements</li>
                    <li>Anyone who wants a more effective online presence without overcomplicating it</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>

          {safeReviews.length > 0 && (
            <div className="bws-gsl-section">
              <div className="bws-gsl-sectionHead">
                <Typography className="bws-gsl-h2">Client feedback</Typography>
              </div>

              <div className="bws-gsl-reviews">
                {safeReviews.slice(0, 3).map((r, idx) => (
                  <Paper key={idx} className="bws-gsl-review" elevation={0}>
                    <Rating
                      value={5}
                      readOnly
                      size="small"
                      className="bws-gsl-reviewStars"
                      aria-label="5 out of 5 stars"
                    />
                    <Typography className="bws-gsl-reviewQuote">“{r.quote}”</Typography>
                    <Typography className="bws-gsl-reviewAuthor">— {r.author}</Typography>
                  </Paper>
                ))}
              </div>
            </div>
          )}

          <div className="bws-gsl-section">
            <Paper className="bws-gsl-faq" elevation={0}>
              <Typography className="bws-gsl-h2">Frequently asked questions</Typography>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Can you help if I already have a website?</Typography>
                <Typography className="bws-gsl-faqA">
                  Yes. A lot of projects are improvements to an existing site rather than a full rebuild.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">Do you only work on full websites?</Typography>
                <Typography className="bws-gsl-faqA">
                  No. Some projects are homepage improvements, landing page updates, Shopify fixes, or restructuring a few important pages.
                </Typography>
              </div>

              <div className="bws-gsl-faqItem">
                <Typography className="bws-gsl-faqQ">What do you need from me to get started?</Typography>
                <Typography className="bws-gsl-faqA">
                  A short description of the site, what is not working well, and what you want the page or website to do better.
                </Typography>
              </div>
            </Paper>
          </div>

          <Paper className="bws-gsl-bottomCta" elevation={0}>
            <div>
              <Typography className="bws-ctaTitle">Need help improving your website?</Typography>
              <p className="bws-muted">
                Send a quick description of your site and what you want it to do better.
              </p>
            </div>
            <Button component={RouterLink} to="/contact" variant="contained" className="bws-btn">
              Contact
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
}
